import { useContext, useCallback } from 'react'

import { apiClient } from '../axiosConfig/apiClient'
import {
  COURIER_EVENTS_URL,
  DOWNLOAD_DELIVERY_CODE_URL,
  SALE_PRODUCTS_URL,
  SEND_TRACKING_NUMBER_URL,
  VERIFY_MAIL_URL,
  WAREHOUSE_EXPANDED_DATA_URL,
  TRACKING_DELIVERY_MAP_URL,
  TRACKING_SALE_URL,
  REPORT_DELIVERY_ISSUE_URL
} from '../constants/endpoints'
import { DataContext } from '../context/DataContext'

export const useApi = () => {
  const {
    sale,
    setSale,
    setLoggedIn,
    setTotalProducts,
    companyData,
    companyColor,
    setTrackingNumber
  } = useContext(DataContext)

  const getSaleWithFlappData = (saleData: Sale) => {
    if (saleData.flappData?.deliveryType === 'pickup') {
      return {
        ...saleData,
        deliveryType: 'storePickup',
        clickAndCollect: true
      }
    }
    return saleData
  }

  const getSale = useCallback(async (saleId: string) => {
    try {
      const response = await apiClient.get(TRACKING_SALE_URL(saleId))
      const { auth, ...saleData } = response.data

      if (!auth) {
        setLoggedIn(false)
        localStorage.removeItem('auth')
      }
      setSale(getSaleWithFlappData(saleData))
      setTrackingNumber(saleData.saleOrder)
      return { success: true }
    } catch (error: any) {
      if (error.response.status === 401) {
        setLoggedIn(false)
        localStorage.removeItem('auth')
      }
      return { error: true }
    }
  }, [setSale, setTrackingNumber, setLoggedIn])

  const getProducts = async () => {
    try {
      const response = await apiClient.get(`${SALE_PRODUCTS_URL(sale?.packageId)}`)
      setTotalProducts(response.data)
      return { success: true }
    } catch (error: any) {
      if (error.response.status === 401) {
        localStorage.removeItem('auth')
      }
      return { error: true }
    }
  }

  const downloadDeliveryCode = async () => {
    try {
      const response = await apiClient.post(`${DOWNLOAD_DELIVERY_CODE_URL}`, {
        packageId: sale?.packageId
      })
      return response.data.qrCode
    } catch (error: any) {
      if (error.response.status === 401) {
        setLoggedIn(false)
        localStorage.removeItem('auth')
      }
      return { error: true }
    }
  }

  const resendSaleOrderId = async (input: string, type: string) => {
    try {
      await apiClient.post(`${SEND_TRACKING_NUMBER_URL}`, { input, type, companyId: companyData.companyId })
      return { success: true }
    } catch (error) {
      console.error('Error re-sending sale order:', error)
      return { error: true }
    }
  }

  const verifyMail = async (modalInput: string, saleId: string, type: string) => {
    try {
      const response = await apiClient.post(`${VERIFY_MAIL_URL}`, {
        input: modalInput,
        saleId,
        type,
        companyId: companyData.companyId
      })
      localStorage.setItem('auth', response.data.token)
      setLoggedIn(true)
      setSale(getSaleWithFlappData(response.data.sale))
      return { authToken: response.data.token }
    } catch (error) {
      console.error('Error verifying mail:', error)
      return { error: 'No se pudo validar la identidad' }
    }
  }

  const getWarehouseExpandedData = async () => {
    try {
      const response = await apiClient.get(`${WAREHOUSE_EXPANDED_DATA_URL(sale?.warehouseId?.toString() ?? '')}`)
      return response.data
    } catch (error: any) {
      if (error.response.status === 401) {
        localStorage.removeItem('auth')
        setLoggedIn(false)
      }
      return { error: true }
    }
  }

  const getDeliveryMap = useCallback(async () => {
    try {
      const response = await apiClient.post(`${TRACKING_DELIVERY_MAP_URL}`, {
        warehouseId: sale?.warehouseId,
        holdingId: sale?.holdingId,
        deliveryLocation: {
          address: sale?.deliveryAddress,
          city: sale?.city,
          stateId: sale?.stateId
        },
        color: companyColor ?? '#000000'
      })
      if (!response.data.showMap) {
        return { showMap: false }
      }
      return { showMap: true, mapUrl: response.data.mapUrl }
    } catch (error: any) {
      if (error.response.status === 401) {
        localStorage.removeItem('auth')
        setLoggedIn(false)
      }
      return { error: true }
    }
  }, [sale, companyColor, setLoggedIn])

  const getCourierEvents = useCallback(async () => {
    try {
      const response = await apiClient.get(COURIER_EVENTS_URL(sale?.pinflagId ?? '', companyData?.companyId ?? ''))
      return response.data
    } catch (error: any) {
      return { error: true }
    }
  }, [sale?.pinflagId, companyData?.companyId])

  const reportDeliveryIssue = useCallback(async (pinflagId: string, issueType: string) => {
    try {
      const response = await apiClient.post(REPORT_DELIVERY_ISSUE_URL, { pinflagId, issueType })
      return response.data
    } catch (error: any) {
      const responseError = error.response?.data?.error || error.response?.data?.message
      return { error: responseError || 'No se pudo enviar el reporte' }
    }
  }, [])

  return {
    downloadDeliveryCode,
    getCourierEvents,
    getDeliveryMap,
    getProducts,
    reportDeliveryIssue,
    getSale,
    getWarehouseExpandedData,
    resendSaleOrderId,
    verifyMail
  }
}

export default useApi
